
import NavigationFooter from '@/core/components/NavigationFooter.vue';
import Spinner from '@/core/components/Spinner.vue';
import TabSelection from '@/core/components/TabSelection.vue';
import {
  BookingType,
  Company,
  CreateBooking,
  DRIVING_LICENCE_BOOKING_OPTION,
  PostBooking,
  Project,
  VEHICLE_BOOKING_COMPANY_SETTING,
} from '@/models/bookings';
import { User } from '@/models/users';
import * as TelliqService from '@/services/telliq.service';
import { GETTERS } from '@/store/getters';
import { MUTATIONS } from '@/store/mutations';
import { emitter } from '@/utils/constants';
import { bookingEndDateReconstruction, bookingStartDateReconstruction, tabNames } from '@/utils/functions';
import moment from 'moment';
import Vue from 'vue';
import BookingProject from '../components/BookingProject.vue';

export default Vue.extend({
  data() {
    return {
      loading: false,
      valid: false,
      search_project: '',
      tab_names: tabNames(),
      tab_selected: BookingType.BUSINESS,
      intention: '',
      booking: {} as CreateBooking,
      projects: [] as Project[],
      project: undefined as number | undefined,
      project_description: undefined as string | undefined,
      show_driving_licence_checkbox: false,
      has_driving_license: false,
      rules: {
        project: [
          (v: number) =>
            this.$data.tab_selected === BookingType.PRIVATE ||
            this.$data.projects.length === 0 ||
            !!v ||
            this.$t('core.rules.required'),
        ],
        description: [
          (v: string) =>
            (this.$data.tab_selected === BookingType.BUSINESS && this.$data.projects.length > 0) ||
            !!v ||
            this.$t('core.rules.required'),
          (v: string) => v.length <= 500 || this.$t('core.rules.max_500_characters'),
        ],
        driving_licence_confirmation: [(v: boolean) => !!v || this.$t('core.rules.required')],
      },
    };
  },

  components: {
    Spinner,
    TabSelection,
    NavigationFooter,
    BookingProject,
  },

  async created() {
    this.booking = this.$store.getters[GETTERS.GET_CREATE_BOOKING];

    this.$store.commit(MUTATIONS.SET_HEADER, {
      title: this.$t('bookings.headers.book_car'),
      subtitle: `${this.$t('bookings.labels.car_name')} · ${this.booking.machine?.name}`,
    });

    this.loading = true;
    await this.loadProjects();
    this.loading = false;

    this.intention = this.booking.intention ?? '';
    if (this.booking.canBePrivate) {
      this.tab_selected = this.booking.isPrivateUse === true ? BookingType.PRIVATE : BookingType.BUSINESS;
    }
  },

  methods: {
    async loadProjects(): Promise<void> {
      try {
        const user: User = this.$store.getters[GETTERS.GET_USER];
        const user_info = await TelliqService.getUserInfo(user.id);
        const projects = await TelliqService.getProjects(user_info.companyId);
        const company = await TelliqService.getCompanyById(user_info.companyId);

        this.defineShowCheckboxConfirmation(company);

        this.projects = projects.filter((project) => {
          if (project.active === false) return false;
          if (project.startUtcTime && moment(project.startUtcTime, 'YYYY-MM-DD HH:mm:ss').isAfter()) return false;
          if (project.endUtcTime && moment(project.endUtcTime, 'YYYY-MM-DD HH:mm:ss').isBefore()) return false;
          // Project type 1 is private.
          if (project.projectType === 1) return false;
          return true;
        });
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
      }
    },

    defineShowCheckboxConfirmation(company: Company): void {
      if (!company.moduleSettings) return;
      const vehicle_booking_settings = company.moduleSettings[VEHICLE_BOOKING_COMPANY_SETTING];
      if (!vehicle_booking_settings?.length) return;
      const driving_licence_option = vehicle_booking_settings.find(
        (setting) => setting.description === DRIVING_LICENCE_BOOKING_OPTION,
      );
      if (!driving_licence_option) return;
      this.show_driving_licence_checkbox = driving_licence_option.value;
    },

    onProjectChanged(project_id: number): void {
      this.project = project_id;
      this.project_description = this.projects.find((project) => project.projectId === project_id)?.description;
      (this.$refs.form as Vue & { validate: () => boolean }).validate();
    },

    onTabChanged(tab_index: number): void {
      this.tab_selected = tab_index;
      this.booking.isPrivateUse = tab_index === BookingType.PRIVATE;
      (this.$refs.form as Vue & { validate: () => boolean }).validate();
      this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.booking);
    },

    async onBookConfirmed(): Promise<void> {
      this.booking.intention = this.intention;
      this.booking.projectId = this.project;
      this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.booking);
      await this.createBooking();
    },

    async createBooking(): Promise<void> {
      this.loading = true;
      try {
        const booking_payload: PostBooking = {
          startDate: this.defineStartTime(),
          endDate: bookingEndDateReconstruction(this.booking),
          machineId: this.booking.machine?.id,
          isPrivateUse: this.booking.canBePrivate && this.tab_selected === BookingType.PRIVATE,
          ...(this.tab_selected === BookingType.BUSINESS && { projectId: this.booking.projectId }),
          intention: this.intention,
          driverHasDriverLicense: this.has_driving_license,
        };
        if (booking_payload.projectId === null || booking_payload.isPrivateUse) {
          delete booking_payload.projectId;
        }
        const booking_id = await TelliqService.createBooking(booking_payload);
        this.booking.id = booking_id;
        this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.booking);

        emitter.emit('success_animation');

        this.$router.push('/booking/overview');
      } catch (e) {
        console.log(e);
        emitter.emit('notify', {
          text: `${this.$t('core.errors.something_went_wrong')}: ${e.response?.data?.Message || e.response?.data}`,
          color: 'error',
        });
      }
      this.loading = false;
    },

    defineStartTime(): string {
      const booking_start_date = bookingStartDateReconstruction(this.booking);
      const now = moment().utcOffset(0, true);
      if (now.isAfter(booking_start_date)) {
        return now.add(5, 'seconds').toISOString();
      }
      return booking_start_date;
    },

    formatTimeOnUI(time: string | undefined): string | undefined {
      if (!time) {
        return time;
      }
      return moment(time, 'HH:mm:ss.SSS').format('HH:mm');
    },

    onStartDateClicked(): void {
      this.$router.push('/booking/start-date');
    },

    onEndDateClicked(): void {
      this.$router.push('/booking/end-date');
    },
  },
});
